import { captureMessage } from "@sentry/react";
import { z } from "zod";

import { nodeDesignsRevisionSchema, type ServerAction } from "@skilitics/nautilus-core-designer-data";

export const unsentActionsStorageKey = (hostId: string) => `skilitics-interact:unsent-revisions:${hostId}`;

const revisionPayload = z.object({ revision: z.number() });

const serverAction = z.discriminatedUnion("type", [
  z.object({ type: z.literal("APPLY_REVISION"), payload: nodeDesignsRevisionSchema }),
  z.object({ type: z.literal("UNDO_REVISION"), payload: revisionPayload }),
  z.object({ type: z.literal("REDO_REVISION"), payload: revisionPayload }),
]);

const storedUnsentActions = z.object({
  appVersion: z.string(),
  actions: z.array(serverAction),
});

type StoredUnsentActions = z.infer<typeof storedUnsentActions>;

export const saveUnsentActions = (hostId: string, unsentActions: ServerAction[]): void => {
  if (unsentActions.length === 0) {
    clearUnsentActions(hostId);
    return;
  }

  const stored: StoredUnsentActions = { appVersion: APP_VERSION, actions: [...unsentActions] };
  localStorage.setItem(unsentActionsStorageKey(hostId), JSON.stringify(stored));
};

export const loadUnsentActions = (hostId: string): StoredUnsentActions | undefined => {
  const raw = localStorage.getItem(unsentActionsStorageKey(hostId));
  if (!raw) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    captureMessage("Stored unsent actions contain invalid JSON. Discarding.", { level: "warning", extra: { error } });
    clearUnsentActions(hostId);
    return undefined;
  }

  const { success, data, error } = storedUnsentActions.safeParse(parsed);
  if (!success) {
    captureMessage("Stored unsent actions do not match schema. Discarding.", {
      level: "warning",
      extra: { issues: error.issues },
    });
    clearUnsentActions(hostId);
    return undefined;
  }

  if (data.appVersion !== APP_VERSION) {
    captureMessage(
      `Discarded stored unsent actions due to app version mismatch (stored: ${data.appVersion}, current: ${APP_VERSION})`,
      "warning",
    );
    clearUnsentActions(hostId);
    return undefined;
  }

  return data;
};

export const clearUnsentActions = (hostId: string): void => {
  localStorage.removeItem(unsentActionsStorageKey(hostId));
};

export const removeFirstUnsentAction = (hostId: string): void => {
  const stored = loadUnsentActions(hostId);
  if (!stored) return;
  stored.actions.splice(0, 1);
  if (stored.actions.length === 0) {
    clearUnsentActions(hostId);
  } else {
    localStorage.setItem(unsentActionsStorageKey(hostId), JSON.stringify(stored));
  }
};

export const hasUnsentActions = (hostId: string): boolean =>
  localStorage.getItem(unsentActionsStorageKey(hostId)) !== null;

// compile-time check that the zod schema stays in sync with ServerAction
type _CheckServerAction = [ServerAction] extends [z.infer<typeof serverAction>]
  ? [z.infer<typeof serverAction>] extends [ServerAction]
    ? true
    : never
  : never;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertServerActionMatch: _CheckServerAction = true;
